import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveUserSetting } from 'reducers/UserSettingsReducer';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions, getAccessPermission } from '../../../reducers/PermissionsReducer';
import { DEFAULT_NAV_PAGE } from '../constants/evidencePreferencesConstants';
import evidenceMenus from '../routes/evidenceMenu';

export const hasAnyPermission = (permissionResult) => {
  if (permissionResult == null) return false;
  if (typeof permissionResult === 'boolean') return permissionResult;
  return Object.values(permissionResult).some(Boolean);
};

export const hasMenuPermission = (item, accessType = 'any') => {
  if (!item.permissionLabel) return true;
  const permissionResult = item.useAccessPermission
    ? getAccessPermission('evidence', item.permissionLabel)
    : getFullPermissions('evidence', item.permissionLabel, 'any');

  if (accessType === 'any') {
    return hasAnyPermission(permissionResult);
  }

  if (typeof permissionResult === 'boolean') {
    return permissionResult;
  }

  return Boolean(permissionResult?.[accessType]);
};

const isPreferenceItem = (item) => item.showInPreferences !== false;

export const getPermittedItems = (items = evidenceMenus, accessType = 'any') =>
  items.filter((item) => hasMenuPermission(item, accessType));

const getReorderableIds = (permittedItems) =>
  permittedItems.filter(isPreferenceItem).map((item) => item.id);

export const normalizeScreens = (screens, reorderableIds) => {
  const known = [...(screens || [])]
    .filter((s) => reorderableIds.includes(s.screenId))
    .sort((a, b) => a.order - b.order)
    .map((s) => ({ screenId: s.screenId, hidden: !!s.hidden, isDefault: !!s.isDefault }));

  const knownIds = known.map((s) => s.screenId);
  const missing = reorderableIds
    .filter((id) => !knownIds.includes(id))
    .map((screenId) => ({ screenId, hidden: false, isDefault: false }));

  const merged = [...known, ...missing];

  if (!merged.some((s) => s.isDefault && !s.hidden)) {
    merged.forEach((s) => {
      s.isDefault = false;
    });
    const fallback =
      merged.find((s) => s.screenId === DEFAULT_NAV_PAGE && !s.hidden) ||
      merged.find((s) => !s.hidden);
    if (fallback) fallback.isDefault = true;
  }

  return merged.map((s, index) => ({ ...s, order: index }));
};

export const getVisibleOrderedMenu = (evidencePreferenceSetting) => {
  const permittedItems = getPermittedItems(evidenceMenus, 'Read');
  const reorderableIds = getReorderableIds(permittedItems);
  const screens = normalizeScreens(evidencePreferenceSetting?.screens, reorderableIds);

  const visibleReorderable = screens
    .filter((s) => !s.hidden)
    .sort((a, b) => a.order - b.order)
    .map((s) => permittedItems.find((item) => item.id === s.screenId))
    .filter(Boolean);

  const fixedRoutes = permittedItems.filter((route) => !isPreferenceItem(route));

  return [...visibleReorderable, ...fixedRoutes];
};

export const getDefaultRoutePath = (evidencePreferenceSetting) => {
  const permittedItems = getPermittedItems(evidenceMenus, 'Read');
  const reorderableIds = getReorderableIds(permittedItems);
  const screens = normalizeScreens(evidencePreferenceSetting?.screens, reorderableIds);
  const defaultScreenId = screens.find((s) => s.isDefault)?.screenId;

  const target = permittedItems.find((item) => item.id === defaultScreenId);
  const fallback = permittedItems.find((item) => item.id === DEFAULT_NAV_PAGE) || permittedItems[0];
  return (target || fallback)?.path;
};

export const useEvidenceNavPreferences = () => {
  const dispatch = useDispatch();
  const saved = useSelector((state) => state.userSettings.evidencePreferenceSetting);

  const permittedItems = useMemo(() => getPermittedItems(evidenceMenus, 'Read'), []);
  const navItemsDef = useMemo(
    () =>
      permittedItems
        .filter(isPreferenceItem)
        .map((item) => ({ id: item.id, label: item.label, Icon: item.icon })),
    [permittedItems]
  );
  const reorderableIds = useMemo(() => navItemsDef.map((item) => item.id), [navItemsDef]);

  const [screens, setScreens] = useState(() => normalizeScreens(saved?.screens, reorderableIds));

  const order = useMemo(
    () => [...screens].sort((a, b) => a.order - b.order).map((s) => s.screenId),
    [screens]
  );
  const hidden = useMemo(() => screens.filter((s) => s.hidden).map((s) => s.screenId), [screens]);
  const defaultId = useMemo(() => screens.find((s) => s.isDefault)?.screenId || DEFAULT_NAV_PAGE, [
    screens,
  ]);

  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizeScreens(screens, reorderableIds)) !==
      JSON.stringify(normalizeScreens(saved?.screens, reorderableIds)),
    [screens, saved, reorderableIds]
  );

  const setOrder = (orderIds) => {
    setScreens((prev) =>
      orderIds.map((screenId, index) => ({
        ...prev.find((s) => s.screenId === screenId),
        order: index,
      }))
    );
  };

  const toggleHidden = (screenId) => {
    setScreens((prev) =>
      normalizeScreens(
        prev.map((s) => (s.screenId === screenId ? { ...s, hidden: !s.hidden } : s)),
        reorderableIds
      )
    );
  };

  const setDefaultId = (screenId) => {
    setScreens((prev) => prev.map((s) => ({ ...s, isDefault: s.screenId === screenId })));
  };

  const savePreferences = () => {
    const normalized = normalizeScreens(screens, reorderableIds);
    setScreens(normalized);
    dispatch(saveUserSetting('evidencePreferenceSetting', { ...saved, screens: normalized }));
    dispatch(notify('Preferences saved', 'success'));
  };

  return {
    navItemsDef,
    order,
    hidden,
    defaultId,
    setOrder,
    toggleHidden,
    setDefaultId,
    isDirty,
    savePreferences,
  };
};
